import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { alpha, Box, Button, Typography, useTheme } from '@mui/material'
import React from 'react'
import { NewDeal } from 'types'

interface ContractTotalsBarProps {
  deal: NewDeal
  monthlyTotal: number
  implementationFee: number
  grossProfit: number
  totalPoints?: number
  setupFee: number
  total: number
  isCSA?: boolean
  paymentAmount?: number
  isEditing: boolean
  onAddProduct: () => void
}

const fmt = (v: number) =>
  v.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

const Stat = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        bgcolor: highlight ? alpha(theme.palette.text.highlight, 0.08) : alpha(theme.palette.text.primary, 0.03),
        border: '1px solid',
        borderColor: highlight ? alpha(theme.palette.text.highlight, 0.2) : 'divider',
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'text.secondary',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 700,
          color: highlight ? 'primary.main' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

const ContractTotalsBar: React.FC<ContractTotalsBarProps> = ({
  deal,
  monthlyTotal,
  implementationFee,
  grossProfit,
  totalPoints,
  setupFee,
  total,
  isCSA,
  paymentAmount,
  isEditing,
  onAddProduct,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        flexWrap: 'wrap',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stat label="Monthly" value={fmt(monthlyTotal)} />
      <Stat label="Setup Fee" value={fmt(setupFee)} />
      {implementationFee > 0 && <Stat label="Impl. Fee" value={fmt(implementationFee)} />}
      <Stat label="Gross Profit" value={fmt(grossProfit)} />
      {totalPoints != null && totalPoints > 0 && <Stat label="Total Points" value={totalPoints.toLocaleString()} />}
      <Stat
        label={isCSA ? 'Per-Payment' : 'Total'}
        value={isCSA && paymentAmount != null ? fmt(paymentAmount) : fmt(total)}
        highlight
      />
      {isEditing && (
        <Box sx={{ marginLeft: 'auto' }}>
          <Button
            size="small"
            sx={{ textTransform: 'none', '&:hover': { opacity: '80%' } }}
            disabled={!deal?.isDraft}
            startIcon={<AddCircleOutlineIcon />}
            onClick={onAddProduct}
          >
            Add Service
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default ContractTotalsBar
